'use client';

import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { SwiperSlide } from 'swiper/react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import {
  hasProfileNoLoyaltyWall,
  hasProfileUserInformation,
} from '@/components/templates/profile/profile.utils';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ProfileModuleList, {
  ProfileModule,
} from '@/components/utilities/content-modules/profile-module-list/profile-module-list';
import { isModuleLocked } from '@/components/utilities/module-lock-wrapper/module-lock-wrapper';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import SwipeItems from '@/components/utilities/swipe-items/swipe-items';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useUserProfileContext } from '@/libraries/contexts/user-profile-context/user-profile-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './profile.module.scss';

export type ProfileProps = Pick<
  Content.Components['ProfilePageDto'],
  '$type' | 'profileSubPages'
> &
  ModuleWrapperProps;

const OFFSET = 32;

const Profile = ({ profileSubPages, ...rest }: ProfileProps) => {
  const { t } = useI18n();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isLaptop = useWindowSize('laptop');
  const swiperRef = useRef<SwiperInstance>();
  const { stickyTopContentHeight } = usePageDetailsContext();
  const { profileSubPageId, setProfileSubPageId } = useUserProfileContext();

  const activeSubPageId = profileSubPageId ?? profileSubPages[0]?.id ?? null;
  const activeSubPage = profileSubPages.find(
    profileSubPage => profileSubPage.id === activeSubPageId,
  );
  const activeContentModules =
    activeSubPage?.sections.flatMap(section =>
      section.items.map(item => item.contentModule as unknown as ProfileModule),
    ) || [];

  const isSectionItemLocked = (
    item: Content.Components['ProfilePageItemDto'],
  ) => {
    return isModuleLocked(
      item.contentModule as ProfileModule,
      activeContentModules,
    );
  };

  const scrollToTop = () => {
    if (!window) return;

    window.scrollTo({ behavior: 'smooth', top: 0 });
  };

  const handleClickMenuItem = (id: string) => {
    setProfileSubPageId(id);

    const params = new URLSearchParams(searchParams!.toString());

    params.set('profileSubPageId', id);

    window.history.replaceState(null, '', `${pathname}?${params.toString()}`);
  };

  useEffect(() => {
    if (!swiperRef.current || !activeSubPageId) return;

    const slideIndex = profileSubPages.findIndex(
      profileSubPage => profileSubPage.id === activeSubPageId,
    );

    if (slideIndex !== -1) {
      swiperRef.current.slideTo(slideIndex);
    }

    scrollToTop();
  }, [activeSubPageId, profileSubPages]);

  const renderSlideItem = (
    profileSubPage: Content.Properties.ProfileSubPage,
  ) => {
    const isActive = profileSubPage.id === activeSubPageId;

    return (
      <button
        key={profileSubPage.id}
        className={classNames(styles.profileSubPage, {
          [styles.isActive]: isActive,
          [styles.isLoyalty]: profileSubPage.isLoyalty,
        })}
        {...(profileSubPage.isLoyalty && { 'data-brand': 'onehome' })}
        onClick={() => handleClickMenuItem(profileSubPage.id)}
      >
        <Icon
          className={styles.menuIcon}
          name={profileSubPage.menuIcon}
          size={16}
        />

        <Typography
          className={styles.menuText}
          data-brand="default"
          tagStyle="bodyLargeBold"
        >
          {profileSubPage.menuTitle}
        </Typography>
      </button>
    );
  };

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container className={styles.container}>
        <Row className={styles.row}>
          <Column width={{ laptop: 3 }}>
            <div
              className={styles.left}
              style={
                isLaptop ? { top: `${stickyTopContentHeight + OFFSET}px` } : {}
              }
            >
              <Typography className={styles.title} tagStyle="headingMedium">
                {t('items.profile.profilePage.myProfile')}
              </Typography>

              <div className={styles.tabs}>
                <SwipeItems swiperRef={swiperRef}>
                  {profileSubPages.map(profileSubPage => (
                    <SwiperSlide
                      key={profileSubPage.id}
                      className={styles.slide}
                    >
                      {renderSlideItem(profileSubPage)}
                    </SwiperSlide>
                  ))}
                </SwipeItems>
              </div>

              <div className={styles.profileSubPages}>
                {profileSubPages.map(renderSlideItem)}
              </div>
            </div>
          </Column>

          <Column width={{ laptop: 9 }}>
            <div key={activeSubPageId} className={styles.right}>
              {activeSubPage &&
                activeSubPage.sections.map(section => (
                  <div
                    key={section.id}
                    className={classNames(styles.section, {
                      [styles.hasProfileNoLoyaltyWall]:
                        hasProfileNoLoyaltyWall(section),
                      [styles.hasProfileUserInformation]:
                        hasProfileUserInformation(section),
                      [styles.isSingle]: section.items.length === 1,
                    })}
                  >
                    {section.items.map(item => (
                      <div
                        key={item.id}
                        className={classNames(styles.item, {
                          [styles.isLocked]: isSectionItemLocked(item),
                        })}
                      >
                        {(item.headline || item.description) && (
                          <div className={styles.itemHeader}>
                            {item.headline && (
                              <Typography
                                className={styles.headline}
                                tagStyle="headingSmall"
                              >
                                {item.headline}
                              </Typography>
                            )}

                            {item.description && (
                              <Typography
                                className={styles.description}
                                tagStyle="bodySmall"
                              >
                                {item.description}
                              </Typography>
                            )}
                          </div>
                        )}

                        {item.contentModule && (
                          <div className={styles.content}>
                            <ProfileModuleList
                              contentModules={[
                                item.contentModule as unknown as ProfileModule,
                              ]}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

Profile.displayName = 'Profile';

export default Profile;
